import { NodeProps, useReactFlow, Node, Edge, getOutgoers } from 'reactflow';

import { createId } from '@paralleldrive/cuid2';
import { JSONContent } from '@tiptap/react';
import { flowEditorState } from '../../flowEditorState';

export function useAddObjectButton({
  id,
  sourceHandle,
  tiptapContent,
  isContainerizedNode = false,
  shouldRenderOutside = false,
}: {
  id?: NodeProps['id'] | undefined;
  sourceHandle?: string | undefined;
  tiptapContent?: JSONContent;
  isContainerizedNode?: boolean;
  shouldRenderOutside?: boolean;
}) {
  const { getNode, setNodes, setEdges, getNodes, getEdges, setCenter } = useReactFlow();

  const onClick = () => {
    if (!id) {
      console.warn('No parent node id');
      return;
    }
    // we need the current node object for getting its position
    const currentNode = getNode(id);
    const nodesCount = getNodes().length;

    if (!currentNode) {
      console.warn('No current node');
      return;
    }

    const newNodeId = createId();

    if (isContainerizedNode) {
      const parentNode = getNode(currentNode.parentNode!) as Node;

      let newNode: Node = {
        id: newNodeId,
        data: { label: `block ${nodesCount + 1}` },
        type: 'block',
        position: {
          x: parentNode.position.x + parentNode.width! + 200,
          y: parentNode.position.y + 200,
        },
        dragHandle: '.drag-handle',
      };

      const newEdge: Edge = {
        id: `${currentNode.id}-${newNode.id}`,
        source: currentNode.id,
        sourceHandle,
        target: newNode.id,
      };

      if (!shouldRenderOutside) {
        newNode = {
          ...newNode,
          position: {
            x: currentNode.position.x + 400,
            y: currentNode.position.y,
          },
          parentNode: parentNode.id,
          extent: 'parent',
          expandParent: true,
        };
      }

      setNodes((nds) =>
        nds
          .map((node) => {
            if (node.id === currentNode.id) {
              node.data = {
                ...node.data,
                outgoers: getOutgoers(
                  currentNode,
                  [...getNodes(), newNode],
                  [...getEdges(), newEdge],
                ).map((n) => n.id),
              };
            }
            return node;
          })
          .concat([newNode]),
      );

      setEdges((edges) => edges.concat([newEdge] as Edge[]));

      flowEditorState.markDirty();

      const centerPosition = shouldRenderOutside
        ? { x: newNode.position.x + 200, y: newNode.position.y + 250 }
        : {
            x: parentNode.position.x + currentNode.position.x + currentNode.width! + 200,
            y: newNode.position.y + 250,
          };
      setCenter(centerPosition.x, centerPosition.y, {
        zoom: 1,
        duration: 800,
      });

      return newNodeId;
    } else {
      const tiptapRoot: JSONContent = {
        type: 'doc',
        content: [],
      };

      if (tiptapContent) {
        tiptapRoot.content?.push(tiptapContent);
      }

      const newNode: Node = {
        id: newNodeId,
        data: tiptapContent
          ? {
              label: `block ${nodesCount + 1}`,
              tiptap: tiptapRoot,
            }
          : { label: `block ${nodesCount + 1}` },
        type: 'block',
        position: {
          x: currentNode.position.x + 500,
          y: currentNode.position.y,
        },
        dragHandle: '.drag-handle',
      };

      const newEdge: Edge = {
        id: `${currentNode.id}-${newNode.id}`,
        source: currentNode.id,
        sourceHandle,
        target: newNode.id,
      };

      setNodes((nds) =>
        nds
          .map((node) => {
            if (node.id === currentNode.id) {
              node.data = {
                ...node.data,
                outgoers: getOutgoers(
                  currentNode,
                  [...getNodes(), newNode],
                  [...getEdges(), newEdge],
                ).map((n) => n.id),
              };
            }
            return node;
          })
          .concat([newNode]),
      );

      setEdges((edges) => edges.concat([newEdge] as Edge[]));

      flowEditorState.markDirty();

      setCenter(newNode.position.x + 200, newNode.position.y + 250, {
        zoom: 1,
        duration: 800,
      });

      return newNodeId;
    }
  };

  return onClick;
}
